import { type RefObject, useLayoutEffect } from 'react';

/**
 * How small the big screen will shrink a screen before it stops trying.
 *
 * Below this the type is too small to read from the back of a room anyway, so
 * a screen that still does not fit is clipped at the bottom of the results
 * area rather than shrunk further. Only an author's worst case reaches it —
 * three questions stacked in one column with ten answers each.
 */
export const FIT_FLOOR = 0.3;

/**
 * The largest scale in [floor, 1] at which `overflows(scale)` is false.
 *
 * Bisection, not a single proportional guess: wrapped lines and fixed-size
 * pieces (a thumbnail's border, a rounded track) mean content does not shrink
 * in exact proportion to the scale, so the measured answer is the only safe
 * one. It does assume content gets no taller as the scale falls, which holds
 * for anything sized in proportion to it. Eight halvings of 0.7 land within
 * 0.003 of the true value, and each is one layout on a screen that changes a
 * few times a presentation, not per vote.
 */
export function fitScale(
  overflows: (scale: number) => boolean,
  floor = FIT_FLOOR,
  steps = 8,
): number {
  if (!overflows(1)) return 1;
  if (overflows(floor)) return floor;

  let fits = floor;
  let tooBig = 1;
  for (let step = 0; step < steps; step += 1) {
    const middle = (fits + tooBig) / 2;
    if (overflows(middle)) tooBig = middle;
    else fits = middle;
  }
  // Rounded down, so the value written is never one that was measured to spill.
  return Math.floor(fits * 1000) / 1000;
}

/**
 * Keeps `content` inside `frame` by writing a `--fit` multiplier on the frame.
 *
 * The CSS does the shrinking: every size inside the frame is multiplied by
 * `var(--fit, 1)`, so this only has to find the number. Written straight onto
 * the element rather than through React state, so a fit never re-renders the
 * results — the bars animate, and a re-render would restart them.
 *
 * Re-fits whenever either box changes size: the viewport, the header growing
 * when the QR code lands, a question from the room appearing above the
 * ballot, an image arriving, a hold or a reveal. A vote changes neither box,
 * so a busy room costs nothing. `resetKey` is whatever remounts `content`.
 *
 * Measures `offsetHeight`, which ignores transforms, so the entrance
 * animation's slide cannot be mistaken for overflow.
 */
export function useFitToHeight(
  frame: RefObject<HTMLElement | null>,
  content: RefObject<HTMLElement | null>,
  resetKey: unknown,
): void {
  useLayoutEffect(() => {
    const box = frame.current;
    const inner = content.current;
    if (!box || !inner) return;

    const apply = (scale: number) => box.style.setProperty('--fit', String(scale));
    const fit = () =>
      apply(
        fitScale((scale) => {
          apply(scale);
          return inner.offsetHeight > box.clientHeight;
        }),
      );

    fit();

    // Absent in jsdom and in nothing a projector runs; without it the screen is
    // fitted once, which is still right until something changes size.
    if (typeof ResizeObserver === 'undefined') return;

    let live = true;
    const observer = new ResizeObserver(() => fit());
    observer.observe(box);
    observer.observe(inner);
    // Text is measured in a fallback face until the webfont arrives.
    void document.fonts?.ready.then(() => {
      if (live) fit();
    });

    return () => {
      live = false;
      observer.disconnect();
    };
  }, [frame, content, resetKey]);
}
